import { faker } from '@faker-js/faker';
import { fakeVersion, generateInitialPaginationApi } from '@ingenico/api-sdk-core/mocks';
import type {
  ApplicationVersionResourceListResponseV2Api,
  ApplicationVersionResourceReadV2Api,
  ApplicationVersionResourceWriteV2Api,
  DownloadPolicyApi,
  GetApplicationVersionArtifactByApplicationVersionIdV2QueryResponseApi,
  GetApplicationVersionByIdV2QueryResponseApi,
  PackageRefApi,
  PaginationApi,
  SearchApplicationVersionsV2MutationResponseApi,
  SearchFieldDetailApi,
  SearchFieldListResponseApi,
  SearchRequestApi,
  SoftwareVersionResourceArtifactItemV2Api,
} from '@ingenico/api-sdk-device-management-private-v2';
import { enumVersionPublicationStatus } from '@ingenico/api-sdk-device-management-private-v2';
import { toNumber } from 'lodash-es';
import type { PathParams, ResponseResolverReturnType, HttpRequestResolverExtras, ResponseResolverInfo } from 'msw';
import { HttpResponse } from 'msw';
import { generateArtifactReadV2Api } from './common';

export async function searchApplicationVersionsV2CustomHandler(
  context: ResponseResolverInfo<HttpRequestResolverExtras<PathParams>>,
): Promise<ResponseResolverReturnType<SearchApplicationVersionsV2MutationResponseApi>> {
  const body = (await context.request.json()) as SearchRequestApi;
  const params = new URL(await context.request.url);
  const [offset, limit] = [
    Number(params.searchParams.get('offset') ?? 0),
    Number(params.searchParams.get('limit') ?? 100),
  ];

  const applicationId: string = body.filters.length > 0 ? body.filters[0].value : faker.string.uuid();

  const applicationVersions: SearchApplicationVersionsV2MutationResponseApi = {
    pagination: generatePaginationApi({
      total: 150,
      offset: offset,
      limit: limit,
    }),
    data: faker.helpers.multiple(() => generateApplicationVersionResourceReadV2(applicationId), { count: limit }),
  };

  return HttpResponse.json<SearchApplicationVersionsV2MutationResponseApi>(applicationVersions);
}

function generateApplicationVersionResourceReadV2(applicationId: string): ApplicationVersionResourceReadV2Api {
  const size = 2 ** faker.number.int({ min: 10, max: 35 });
  return {
    applicationDependencyVersionIds: faker.helpers.multiple(faker.string.uuid),
    applicationId,
    artifacts: generateApplicationVersionArtifacts(),
    compatibleOsVersionIds: faker.helpers.multiple(faker.string.uuid),
    creationDate: faker.date.past(),
    id: faker.string.uuid(),
    longDescription: markdownMock,
    publisherId: faker.string.uuid(),
    shortDescription: faker.commerce.productName(),
    size,
    systemPackageDependencyVersionIds: faker.helpers.multiple(faker.string.uuid),
    versionNumber: faker.system.semver(),
    templates: [
      ...faker.helpers.multiple(() => ({
        artifactId: faker.string.uuid(),
        targetLocation: faker.system.filePath(),
      })),
    ],
    status: faker.helpers.arrayElement(Object.values(enumVersionPublicationStatus)),
  };
}

export function generateApplicationVersionArtifacts(): GetApplicationVersionByIdV2QueryResponseApi['artifacts'] {
  const artifactsCount = faker.number.int({ min: 1, max: 4 });

  return Array(artifactsCount)
    .fill(undefined)
    .map((_, index) => ({
      id: faker.string.uuid(),
      order: index,
    })) satisfies GetApplicationVersionByIdV2QueryResponseApi['artifacts'];
}

const mockedArtifacts = Array(faker.number.int({ min: 50, max: 100 }))
  .fill(null)
  .map(() => generateArtifactReadV2Api());

function generateDownloadPolicies(artifacts?: SoftwareVersionResourceArtifactItemV2Api[]): DownloadPolicyApi[] {
  return artifacts
    ?.map(({ id: artifactId }) => {
      const shouldGenerate = faker.helpers.arrayElement([true, false]);
      if (shouldGenerate) {
        const actionsCount = faker.number.int({ min: 1, max: 3 });

        return {
          artifactId,
          actions: [
            {
              deletePackages: Array(actionsCount)
                .fill(undefined)
                .map(
                  () =>
                    ({
                      name: faker.lorem.word(),
                      version: fakeVersion(),
                    }) satisfies PackageRefApi,
                ),
            },
          ],
        } satisfies DownloadPolicyApi;
      } else {
        return null;
      }
    })
    .filter(Boolean) as DownloadPolicyApi[];
}

export function getApplicationVersionByIdV2InitialData(): GetApplicationVersionByIdV2QueryResponseApi {
  return {
    applicationDependencyVersionIds: [faker.string.uuid()],
    applicationId: faker.string.uuid(),
    artifacts: generateApplicationVersionArtifacts(),
    compatibleOsVersionIds: [faker.string.uuid()],
    creationDate: faker.date.recent(),
    id: faker.string.uuid(),
    longDescription: markdownMock,
    publisherId: faker.string.uuid(),
    shortDescription: markdownMock,
    size: 1.5,
    status: faker.helpers.arrayElement(Object.values(enumVersionPublicationStatus)),
    systemPackageDependencyVersionIds: [faker.string.uuid()],
    templates: [
      ...faker.helpers.multiple(() => ({
        artifactId: faker.string.uuid(),
        targetLocation: faker.system.filePath(),
      })),
    ],
    versionNumber: '1.0.0',
    downloadPolicies: generateDownloadPolicies(mockedArtifacts),
  };
}

export const markdownMock =
  '**Version 3.5.0 - June 5, 2024**\n' +
  '\n' +
  '- New Features:\n' +
  '  - Introduced support for NFC payments for a seamless tap-and-go experience.\n' +
  '  - Added multi-language support with 5 new languages: Spanish, French, German, Italian, and Japanese.\n' +
  '  - Enhanced dashboard with real-time transaction analytics.\n' +
  '\n' +
  '- Improvements:\n' +
  '  - Optimized transaction processing speed, reducing average time by 30%.\n' +
  '  - Improved UI for easier navigation and better accessibility.\n' +
  '  - Upgraded security protocols to comply with the latest PCI DSS standards.\n' +
  '\n' +
  '- Bug Fixes:\n' +
  '  - Fixed issue causing occasional crashes during batch settlement.\n' +
  '  - Resolved display errors in the transaction history.\n' +
  '  - Corrected alignment issues in printed receipts.';

export function getApplicationSearchFieldsV2Handler(): SearchFieldDetailApi[] {
  const keys = [
    'id',
    'name',
    'shortDescription',
    'longDescription',
    'status',
    'publisherId',
    'providerId',
    'creationDate',
    'type',
    'compatibleDeviceFamilyNames',
  ];
  return keys.map((key) => ({ key: `application.${key}`, type: 'string' }));
}

export function getApplicationSearchFieldsV2CustomHandlerMock() {
  return HttpResponse.json<SearchFieldListResponseApi>(getApplicationSearchFieldsV2Handler());
}

export function getApplicationVersionArtifactByApplicationVersionIdV2ResponseMock(): GetApplicationVersionArtifactByApplicationVersionIdV2QueryResponseApi {
  return {
    pagination: generateInitialPaginationApi(mockedArtifacts.length),
    data: mockedArtifacts,
  };
}
const qttLines = faker.number.int({ min: 10, max: 30 });

export function searchApplicationVersionCustomHandler(
  context: ResponseResolverInfo<HttpRequestResolverExtras<PathParams>>,
) {
  const url = new URL(context.request.url);

  const offsetParam = url.searchParams.get('offset') ?? 0;
  const limitParam = url.searchParams.get('limit') ?? 10;

  const offset = toNumber(offsetParam);
  const limit = toNumber(limitParam);

  return HttpResponse.json<ApplicationVersionResourceListResponseV2Api>(searchApplicationVersion({ limit, offset }));
}

function searchApplicationVersion({
  offset,
  limit,
}: {
  readonly offset: number;
  readonly limit: number;
}): ApplicationVersionResourceListResponseV2Api {
  return {
    data: [
      ...faker.helpers.multiple(() => generateApplicationVersion(), { count: limit - 1 }),
      generateApplicationVersion('8c37dbbb-fee4-4a0b-89a1-24983f5c1fFF'),
    ],
    pagination: generatePaginationApi({ total: qttLines, offset, limit }),
  };
}

function generatePaginationApi({
  total,
  offset,
  limit,
}: {
  readonly total: number;
  readonly offset: number;
  readonly limit: number;
}): PaginationApi {
  const initialTerm = Math.ceil(offset / limit) + 1;
  return {
    totalResults: total,
    startIndex: initialTerm,
    itemsPerPage: limit,
  };
}

function generateApplicationVersion(id?: string): ApplicationVersionResourceReadV2Api {
  return {
    applicationDependencyVersionIds: [],
    applicationId: faker.string.uuid(),
    artifacts: generateArtifacts(),
    creationDate: faker.date.past(),
    id: id ?? faker.string.uuid(),
    publisherId: faker.string.uuid(),
    size: faker.number.int(),
    status: 'PUBLISHED',
    templates: generateTemplates(),
    versionNumber: id ? '1.0.0' : fakeVersion(),
  };
}

function generateArtifacts(): ApplicationVersionResourceReadV2Api['artifacts'] {
  const artifactsCount = faker.number.int({ min: 1, max: 4 });

  return Array(artifactsCount)
    .fill(undefined)
    .map((_, index) => ({
      id: faker.string.uuid(),
      order: index,
    })) satisfies ApplicationVersionResourceReadV2Api['artifacts'];
}

function generateTemplates(): ApplicationVersionResourceReadV2Api['templates'] {
  const artifactsCount = faker.number.int({ min: 1, max: 3 });

  return Array(artifactsCount)
    .fill(undefined)
    .map(() => ({
      artifactId: faker.string.uuid(),
    })) satisfies ApplicationVersionResourceReadV2Api['templates'];
}

/**
 * Custom handler for POST /device-management/v2/application-versions
 * Creates a new application version and returns the created resource.
 */
export async function createApplicationVersionV2CustomHandler(
  context: ResponseResolverInfo<HttpRequestResolverExtras<PathParams>>,
): Promise<ResponseResolverReturnType<ApplicationVersionResourceReadV2Api>> {
  const data = (await context.request.json()) as ApplicationVersionResourceWriteV2Api;

  return HttpResponse.json<ApplicationVersionResourceReadV2Api>({
    id: faker.string.uuid(),
    applicationId: data.applicationId,
    applicationDependencyVersionIds: data.applicationDependencyVersionIds,
    artifacts: data.artifacts ?? [],
    compatibleOsVersionIds: data.compatibleOsVersionIds,
    creationDate: new Date(),
    longDescription: data.longDescription,
    publisherId: faker.string.uuid(),
    shortDescription: data.shortDescription,
    size: faker.number.int({ min: 1000, max: 100000 }),
    status: data.status ?? 'PUBLISHED',
    systemPackageDependencyVersionIds: data.systemPackageDependencyVersionIds,
    templates: data.templates,
    versionNumber: data.versionNumber,
  });
}
